import quickFetch from 'quick-fetch';
import stableStringify from 'fast-json-stable-stringify';
import { makeChirpEarlyRequest } from 'rpc-client-utils';
import type {
  CrmObjectSearchResponse,
  SearchCrmObjectsRequestType,
} from '../../../__generated__/chirp/com/hubspot/sales/rpc/ViewServiceRpc';
import { searchCrmObjectsEarlyRequestRpc } from '../../../__generated__/chirp/com/hubspot/sales/rpc/earlyRequest/ViewServiceRpc.early';
import {
  CrmObject,
  CrmSearchAllPropertiesArgs,
  CrmSearchResults,
} from './types';
import { convertLegacyObject } from '../CrmObject/convertLegacyObject';
import type { LegacyCrmObject } from '../CrmObject/types';
import { SortContext } from './sortPerfBuffer';
import { normalizeVariables } from './sortFilterGroups';
import { readUserIdFromAuth, type GetUserId } from './getUserId';
import { isSearchRpcGateEnabled } from './gate';

export const SALES_VIEWS_SEARCH_URL =
  '/sales-views/v2/crm-search/with-placeholders';

export type SalesViewsSearchResponse = {
  total: number;
  offset: number;
  hasMore: boolean;
  results: LegacyCrmObject[];
};

export const buildSalesViewsSearchBody = (
  args: CrmSearchAllPropertiesArgs,
  userId?: number
) => ({
  ...args,
  requestOptions: {
    allPropertiesFetchMode: 'latest_version',
    includeAllValues: true,
    includeCurrentUserPermissions: true,
  },
  ...(userId != null ? { crmSearchRequestOptions: { userId } } : {}),
});

export const handleSalesViewsResult = (
  response: SalesViewsSearchResponse
): CrmSearchResults<CrmObject> => ({
  total: response.total,
  offset: response.offset,
  hasMore: response.hasMore,
  results: response.results.map(convertLegacyObject),
});

export const buildSearchCrmObjectsRequest = (
  args: CrmSearchAllPropertiesArgs
): SearchCrmObjectsRequestType => args as SearchCrmObjectsRequestType;

export const handleSearchCrmObjectsResult = (
  response: CrmObjectSearchResponse
): CrmSearchResults<CrmObject> => ({
  total: response.total,
  offset: response.offset,
  hasMore: response.hasMore,
  results: response.results.map(
    (object) =>
      ({
        objectId: object.objectId,
        objectTypeId: object.objectTypeId,
        currentUserPermissions: object.currentUserPermissions,
        properties: Object.fromEntries(
          Object.entries(object.properties).map(([key, value]) => [
            key,
            {
              name: key,
              value: value.value ?? undefined,
              sourceId: value.sourceId ?? undefined,
              source: value.source ?? undefined,
              timestamp: value.timestamp ?? undefined,
            },
          ])
        ),
      }) as CrmObject
  ),
});

export const allPropertiesRequestName = (
  variables: CrmSearchAllPropertiesArgs,
  context: SortContext
) =>
  `hs-resources:crmSearchAllProperties:${stableStringify(
    normalizeVariables(variables, context)
  )}`;

export const earlyRequestAllProperties = async ({
  variables,
  getUserId = readUserIdFromAuth,
}: {
  variables: CrmSearchAllPropertiesArgs;
  getUserId?: GetUserId;
}): Promise<void> => {
  const requestName = allPropertiesRequestName(variables, 'early');

  if (quickFetch.getRequestStateByName(requestName)) {
    return;
  }

  if (await isSearchRpcGateEnabled()) {
    makeChirpEarlyRequest(searchCrmObjectsEarlyRequestRpc, {
      request: buildSearchCrmObjectsRequest(variables),
      earlyRequestName: requestName,
    }).catch(() => undefined);
    return;
  }

  let userId: number | undefined;
  try {
    userId = await getUserId();
  } catch {
    // Couldn't resolve the user (e.g. auth timed out) — skip the early request
    // rather than prime one that could return unscoped data. The resolver
    // fallback fetches with a properly resolved userId.
    return;
  }

  // Re-check after awaiting auth: a concurrent call for the same search may
  // have primed it while we waited (continuations resume sequentially, so the
  // first to reach makeEarlyRequest wins and the rest bail here).
  if (quickFetch.getRequestStateByName(requestName)) {
    return;
  }

  quickFetch.makeEarlyRequest(requestName, {
    url: quickFetch.getApiUrl(SALES_VIEWS_SEARCH_URL),
    type: 'POST',
    contentType: 'application/json',
    dataType: 'json',
    data: JSON.stringify(buildSalesViewsSearchBody(variables, userId)),
  });
};

export const consumeEarlyCrmSearchAllProperties = (
  variables: CrmSearchAllPropertiesArgs
): Promise<CrmSearchResults<CrmObject>> => {
  const requestName = allPropertiesRequestName(variables, 'standard');
  const requestState = quickFetch.getRequestStateByName(requestName);

  if (!requestState) {
    return Promise.reject(new Error(`Early request not found: ${requestName}`));
  }

  return new Promise<SalesViewsSearchResponse>((resolve, reject) => {
    requestState.whenFinished((result: SalesViewsSearchResponse) => {
      resolve(result);
      quickFetch.removeEarlyRequest(requestName);
    });
    requestState.onError((_xhr: unknown, message: string) => {
      reject(new Error(`Early request failed: ${message}`));
      quickFetch.removeEarlyRequest(requestName);
    });
  }).then(handleSalesViewsResult);
};
